// src/pages/motolivraison/categories.page.jsx
//
// Page admin pour gérer les catégories de commerces du module MotoLivraison.
// CRUD complet : liste, créer, éditer, toggle-active, supprimer.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Modal, Form, message, Button, Card, Row, Col, Switch,
  Tag, Tooltip, Space, InputNumber, Breadcrumb, Statistic, Avatar, Upload
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusOutlined, AppstoreOutlined,
  RightOutlined, CheckCircleOutlined, CloseCircleOutlined, UploadOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { motolivraisonService } from '../../common/services';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  // Image (optionnelle) : fichier brut choisi + URL d'aperçu (existante ou objet local)
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const onPickImage = (file) => {
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    return false; // empêche l'upload auto d'AntD
  };
  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const stats = {
    total: categories.length,
    actives: categories.filter(c => c.isActive).length,
    inactives: categories.filter(c => !c.isActive).length,
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.listCategories();
      setCategories(data?.categories || []);
    } catch (e) {
      message.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ couleur: '#E5A00D', ordre: 0, isActive: true });
    clearImage();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      code: record.code,
      nom: record.nom,
      description: record.description,
      icone: record.icone,
      couleur: record.couleur,
      ordre: record.ordre,
      isActive: record.isActive,
    });
    setImageFile(null);
    setImagePreview(record.image || null);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Construire le payload : FormData si une nouvelle image est choisie, sinon JSON.
      let payload;
      if (imageFile) {
        payload = new FormData();
        Object.entries(values).forEach(([k, v]) => {
          if (v !== undefined && v !== null) payload.append(k, v);
        });
        payload.append('image', imageFile);
      } else {
        payload = { ...values };
        // Suppression explicite : l'image existante a été retirée sans en choisir une nouvelle.
        if (editing && editing.image && !imagePreview) {
          payload.image = '';
        }
      }

      if (editing) {
        await motolivraisonService.updateCategorie(editing._id, payload);
        message.success('Catégorie mise à jour');
      } else {
        await motolivraisonService.createCategorie(payload);
        message.success('Catégorie créée');
      }
      setModalOpen(false);
      fetchCategories();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleToggle = async (record) => {
    try {
      await motolivraisonService.toggleActiveCategorie(record._id);
      message.success(record.isActive ? 'Catégorie désactivée' : 'Catégorie activée');
      fetchCategories();
    } catch (e) {
      message.error('Erreur lors du changement d\'état');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Supprimer cette catégorie ?',
      content: `La catégorie "${record.nom}" sera supprimée définitivement. Impossible si des commerces l'utilisent.`,
      okText: 'Supprimer',
      okButtonProps: { danger: true },
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await motolivraisonService.deleteCategorie(record._id);
          message.success('Catégorie supprimée');
          fetchCategories();
        } catch (e) {
          message.error(e?.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Catégorie',
      key: 'cat',
      render: (_, r) => (
        <Space>
          <Avatar
            size={40}
            src={r.image || undefined}
            style={{ backgroundColor: r.couleur || '#E5A00D' }}
            icon={<AppstoreOutlined />}
          />
          <div>
            <div style={{ fontWeight: 600 }}>{r.nom}</div>
            <Tag style={{ marginTop: 2 }}>{r.code}</Tag>
          </div>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (v) => <span style={{ color: '#595959' }}>{v || '—'}</span>,
    },
    {
      title: 'Ordre',
      dataIndex: 'ordre',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.ordre - b.ordre,
    },
    {
      title: 'Statut',
      key: 'status',
      width: 120,
      align: 'center',
      render: (_, r) => (
        <Tooltip title={r.isActive ? 'Cliquer pour désactiver' : 'Cliquer pour activer'}>
          <Switch
            checked={r.isActive}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
            onChange={() => handleToggle(r)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, r) => (
        <Space>
          <Tooltip title="Modifier">
            <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(r)} />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(r)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined style={{ fontSize: 12 }} />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Catégories</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={8}>
            <Card><Statistic title="Total" value={stats.total} prefix={<AppstoreOutlined />} /></Card>
          </Col>
          <Col xs={8}>
            <Card><Statistic title="Actives" value={stats.actives} valueStyle={{ color: '#52c41a' }} /></Card>
          </Col>
          <Col xs={8}>
            <Card><Statistic title="Inactives" value={stats.inactives} valueStyle={{ color: '#8c8c8c' }} /></Card>
          </Col>
        </Row>

        <Card
          title="Catégories de commerces"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Nouvelle catégorie
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={categories.map(c => ({ ...c, key: c._id }))}
            loading={loading}
            pagination={{ pageSize: 20 }}
          />
        </Card>
      </div>

      <Modal
        title={editing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Enregistrer"
        cancelText="Annuler"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Code (unique, en minuscules)"
                rules={[{ required: true, message: 'Le code est requis' }]}
              >
                <Input placeholder="ex: restauration" disabled={!!editing} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="nom"
                label="Nom affiché"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input placeholder="Restauration" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Restaurant, maquis, boulangerie..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="icone" label="Icône (Material/Font)">
                <Input placeholder="restaurant" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="couleur" label="Couleur (hex)">
                <Input placeholder="#FF6B35" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="ordre" label="Ordre">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Image (optionnel)">
            <Space align="start">
              {imagePreview ? (
                <div style={{ position: 'relative' }}>
                  <img
                    src={imagePreview}
                    alt="aperçu"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
                  />
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={clearImage}
                    style={{ position: 'absolute', top: -8, right: -8, background: '#fff', borderRadius: '50%' }}
                  />
                </div>
              ) : (
                <Avatar size={80} shape="square" style={{ backgroundColor: '#f0f0f0' }} icon={<AppstoreOutlined />} />
              )}
              <Upload beforeUpload={onPickImage} showUploadList={false} accept="image/*" maxCount={1}>
                <Button icon={<UploadOutlined />}>{imagePreview ? 'Changer l\'image' : 'Choisir une image'}</Button>
              </Upload>
            </Space>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
              Facultatif. Sans image, l'icône ci-dessus est utilisée par défaut.
            </div>
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;
